import React, { type ComponentProps, forwardRef } from "react";
import {
    Modal as AppBridgeModal,
    SaveBar as AppBridgeSaveBar,
    TitleBar as AppBridgeTitleBar,
} from "@shopify/app-bridge-react";

type SaveBarProps = ComponentProps<typeof AppBridgeSaveBar>;
type ModalProps = ComponentProps<typeof AppBridgeModal>;
type TitleBarProps = ComponentProps<typeof AppBridgeTitleBar>;

const isEmbeddedAdminFrame = () => {
    if (typeof window === "undefined") return false;

    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
};

const canUseShopifyUiElement = (elementName: string) =>
    isEmbeddedAdminFrame()
    && typeof window !== "undefined"
    && typeof window.customElements !== "undefined"
    && !!window.customElements.get(elementName);

export const SafeSaveBar = ({ open, children, ...props }: SaveBarProps) => {
    if (canUseShopifyUiElement("ui-save-bar")) {
        return (
            <AppBridgeSaveBar open={open} {...props}>
                {children}
            </AppBridgeSaveBar>
        );
    }

    if (!open) return null;

    return (
        <div className="pf-safe-save-bar" role="status" aria-live="polite">
            <span className="pf-safe-save-bar__message">Unsaved changes</span>
            <div className="pf-safe-save-bar__actions">{children}</div>
        </div>
    );
};

export const SafeAppBridgeModal = forwardRef<HTMLElement, ModalProps>(({
    open,
    children,
    id,
    onHide,
    variant,
    ...props
}, ref) => {
    if (canUseShopifyUiElement("ui-modal")) {
        return (
            <AppBridgeModal
                ref={ref as React.Ref<any>}
                id={id}
                open={open}
                onHide={onHide}
                variant={variant}
                {...props}
            >
                {children}
            </AppBridgeModal>
        );
    }

    if (!open) return null;

    return (
        <div className={`pf-safe-modal pf-safe-modal--${variant ?? "base"}`} role="presentation">
            <button
                type="button"
                className="pf-safe-modal__backdrop"
                aria-label="Close modal"
                onClick={onHide}
            />
            <section
                className="pf-safe-modal__panel"
                role="dialog"
                aria-modal="true"
                id={id}
            >
                {children}
            </section>
        </div>
    );
});

SafeAppBridgeModal.displayName = "SafeAppBridgeModal";

export const SafeAppBridgeTitleBar = forwardRef<HTMLElement, TitleBarProps>(({
    title,
    children,
    ...props
}, ref) => {
    if (isEmbeddedAdminFrame()) {
        return (
            <AppBridgeTitleBar title={title} {...props}>
                {children}
            </AppBridgeTitleBar>
        );
    }

    return (
        <div className="pf-safe-title-bar" ref={ref as React.Ref<HTMLDivElement>}>
            {title ? <h2 className="pf-safe-title-bar__title">{title}</h2> : null}
            {children ? <div className="pf-safe-title-bar__actions">{children}</div> : null}
        </div>
    );
});

// App Bridge Modal inspects direct React children before rendering them.
// Exposing this displayName lets the safe wrapper be recognized as the modal title/action bar.
SafeAppBridgeTitleBar.displayName = "ui-title-bar";
